(() => {
  const ROOT_MARGIN = '0px 0px -10% 0px';

  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._formId = this.getAttribute('form');
      this._form = document.forms[this._formId];
      this._sentinel = null;
      this._observer = null;
      this._shouldShow = false;
      this._isAvailable = !this.hasAttribute('hidden');

      this._stickyQuantity = this.querySelector('[data-sticky-quantity]');
      this._mainQuantity = () => document.getElementById(`${this._formId}-quantity`);
      this._priceContainer = this.querySelector('[data-sticky-bar-price]');
      this._titleEl = this.querySelector('.product-sticky-bar__title');
      this._mediaEl = this.querySelector('.product-sticky-bar__media');
      this._submitButton = this.querySelector('buy-buttons button[type="submit"]');

      this._onResize = this._debounce(this._handleResize.bind(this), 150);
      this._onVariantChange = this._handleVariantChange.bind(this);
      this._onRerender = this._handleRerender.bind(this);
      this._bindQuantitySync();
      this._submitButton?.addEventListener('click', () => this._syncQuantities());
      this._updatePriceForQuantity(this._getQuantity());
      this._setupObserver();

      window.addEventListener('resize', this._onResize);

      if (this._form) {
        this._form.addEventListener('variant:change', this._onVariantChange);
        this._form.addEventListener('product:rerender', this._onRerender);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._observer?.disconnect();

      if (this._form) {
        this._form.removeEventListener('variant:change', this._onVariantChange);
        this._form.removeEventListener('product:rerender', this._onRerender);
      }
    }

    _debounce(fn, delay) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
      };
    }

    _getSentinel() {
      return document.querySelector('.product-info__buy-buttons-sentinel');
    }

    _setupObserver() {
      this._observer?.disconnect();
      this._sentinel = this._getSentinel();

      if (!this._sentinel) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this._shouldShow = entry ? !entry.isIntersecting : false;
          this._updateVisibility();
        },
        { threshold: 0, rootMargin: ROOT_MARGIN }
      );

      this._observer.observe(this._sentinel);
    }

    _handleResize() {
      this._setupObserver();
      this._updateVisibility();
    }

    _updateVisibility() {
      const isVisible = this._shouldShow && this._isAvailable;
      this.classList.toggle('is-visible', isVisible);
    }

    _handleVariantChange(event) {
      const variant = event.detail?.variant;

      if (!variant?.available) {
        this._isAvailable = false;
        this.hidden = true;
        this._updateVisibility();
        return;
      }

      this._isAvailable = true;
      this.hidden = false;
      this._cacheUnitPrices(variant);
      this._updateMedia(variant);
      requestAnimationFrame(() => this._syncFromMain());
      this._updateVisibility();
    }

    _handleRerender() {
      requestAnimationFrame(() => {
        this._syncFromMain();
      });
    }

    _syncFromMain() {
      this._syncPriceFromMain();
      this._syncTitleFromMain();
      this._syncButtonFromMain();
      this._syncQuantityRulesFromMain();
      this._updatePriceForQuantity(this._getQuantity());
    }

    _getQuantity() {
      const value = parseInt(this._stickyQuantity?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    _cacheUnitPrices(variant) {
      if (!variant) return;

      this._unitPrice = variant.price;
      this._unitComparePrice = variant.compare_at_price || 0;

      if (this._priceContainer) {
        this._priceContainer.dataset.unitPrice = String(variant.price);
        this._priceContainer.dataset.unitComparePrice = String(this._unitComparePrice);
      }
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      const parts = amount.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

      if (parts[1] === '00') {
        return `${parts[0]},-`;
      }

      return `${parts[0]},${parts[1]}`;
    }

    _setPriceElementText(element, cents) {
      if (!element) return;

      const formatted = this._formatMoney(cents);
      const srOnly = element.querySelector('.sr-only');

      if (srOnly) {
        element.textContent = '';
        element.appendChild(srOnly);
        element.append(formatted);
      } else {
        element.textContent = formatted;
      }
    }

    _updatePriceForQuantity(quantity) {
      const container = this._priceContainer;
      if (!container) return;

      const unitPrice = parseInt(container.dataset.unitPrice || this._unitPrice, 10);
      const unitComparePrice = parseInt(container.dataset.unitComparePrice || this._unitComparePrice || 0, 10);

      if (!Number.isFinite(unitPrice)) return;

      const salePriceEl = container.querySelector('sale-price');
      const comparePriceEl = container.querySelector('compare-at-price');

      this._setPriceElementText(salePriceEl, unitPrice * quantity);

      if (comparePriceEl && unitComparePrice > unitPrice) {
        comparePriceEl.hidden = false;
        this._setPriceElementText(comparePriceEl, unitComparePrice * quantity);
      } else if (comparePriceEl) {
        comparePriceEl.hidden = true;
      }
    }

    _syncPriceFromMain() {
      const mainPrice = document.querySelector('.product-info__price .price-list');
      const target = this._priceContainer?.querySelector('.price-list');

      if (!mainPrice || !target) return;

      target.replaceWith(mainPrice.cloneNode(true));
      this._priceContainer = this.querySelector('[data-sticky-bar-price]');
    }

    _syncTitleFromMain() {
      const mainTitle = document.querySelector('.product-info__title');
      if (!mainTitle || !this._titleEl) return;

      this._titleEl.textContent = mainTitle.textContent.trim();
    }

    _syncButtonFromMain() {
      const mainButton = document.querySelector('.product-info__buy-buttons buy-buttons button[type="submit"]');
      if (!mainButton || !this._submitButton) return;

      this._submitButton.disabled = mainButton.disabled;
      this._submitButton.innerHTML = mainButton.innerHTML;
    }

    _syncQuantityRulesFromMain() {
      const mainQuantity = this._mainQuantity();
      if (!mainQuantity || !this._stickyQuantity) return;

      ['min', 'max', 'step', 'value'].forEach((attr) => {
        if (mainQuantity.hasAttribute(attr)) {
          this._stickyQuantity.setAttribute(attr, mainQuantity.getAttribute(attr));
        } else {
          this._stickyQuantity.removeAttribute(attr);
        }
      });

      if (mainQuantity.value !== this._stickyQuantity.value) {
        this._stickyQuantity.value = mainQuantity.value;
      }
    }

    _updateMedia(variant) {
      const featuredMedia = variant?.featured_media;
      const image = this.querySelector('.product-sticky-bar__image');

      if (!featuredMedia?.preview_image?.src || !image) return;

      const src = featuredMedia.preview_image.src;
      image.src = src.includes('?') ? `${src}&width=160` : `${src}?width=160`;
      if (featuredMedia.preview_image.width) {
        image.width = featuredMedia.preview_image.width;
      }
      if (featuredMedia.preview_image.height) {
        image.height = featuredMedia.preview_image.height;
      }
      if (featuredMedia.alt != null) {
        image.alt = featuredMedia.alt;
      }
    }

    _bindQuantitySync() {
      if (!this._stickyQuantity) return;

      this._stickyQuantity.addEventListener('change', () => {
        this._syncQuantities();
        this._updatePriceForQuantity(this._getQuantity());
      });

      const mainQuantity = this._mainQuantity();
      mainQuantity?.addEventListener('change', () => {
        if (document.activeElement !== this._stickyQuantity && mainQuantity.value !== this._stickyQuantity.value) {
          this._stickyQuantity.value = mainQuantity.value;
          this._updatePriceForQuantity(this._getQuantity());
        }
      });
    }

    _syncQuantities() {
      if (!this._stickyQuantity || !this._form) return;

      const mainQuantity = this._mainQuantity();

      if (mainQuantity) {
        mainQuantity.value = this._stickyQuantity.value;
        mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      let quantityInput = this._form.querySelector('[name="quantity"]');

      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        this._form.appendChild(quantityInput);
      }

      quantityInput.value = this._stickyQuantity.value;
    }
  }

  if (!window.customElements.get('product-sticky-bar')) {
    window.customElements.define('product-sticky-bar', ProductStickyBar);
  }
})();
